import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { getPendingSkiTransfers, acceptSkiTransfer, rejectSkiTransfer } from '../api';

export default function SkiTransferNotifications({ onTransferAccepted }) {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [processing, setProcessing] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    loadTransfers();
  }, []);

  const loadTransfers = async () => {
    try {
      const data = await getPendingSkiTransfers();
      setTransfers(data);
    } catch (err) {
      console.error('Error loading transfers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!selectedTransfer) return;
    setProcessing(true);
    try {
      console.log('Accepting transfer:', selectedTransfer.id, selectedTransfer);
      await acceptSkiTransfer(selectedTransfer.id);
      await loadTransfers();
      setDetailOpen(false);
      setSelectedTransfer(null);
      if (onTransferAccepted) {
        onTransferAccepted();
      }
    } catch (err) {
      console.error('Error accepting transfer:', err);
      console.error('Transfer ID:', selectedTransfer.id);
      console.error('Transfer object:', selectedTransfer);
      alert(err.message || 'Failed to accept transfer');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedTransfer) return;
    setProcessing(true);
    try {
      await rejectSkiTransfer(selectedTransfer.id);
      await loadTransfers();
      setDetailOpen(false);
      setSelectedTransfer(null);
    } catch (err) {
      console.error('Error rejecting transfer:', err);
      alert(err.message || 'Failed to reject transfer');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewDetails = (transfer) => {
    setSelectedTransfer(transfer);
    setDetailOpen(true);
  };

  if (loading) {
    return null;
  }

  if (transfers.length === 0) {
    return null;
  }

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Alert 
          severity="info" 
          sx={{ 
            mb: 2,
            '& .MuiAlert-message': { width: '100%' }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              You have {transfers.length} pending ski transfer{transfers.length > 1 ? 's' : ''}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleViewDetails(transfers[0])}
            >
              View Details
            </Button>
          </Box>
        </Alert>
      </Box>

      <Dialog 
        open={detailOpen} 
        onClose={() => !processing && setDetailOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Ski Transfer Request
        </DialogTitle>
        <DialogContent>
          {selectedTransfer && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>From</Typography>
                <Typography>{selectedTransfer.from_user_name} ({selectedTransfer.from_user_email})</Typography>
              </Paper>

              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>Ski Details</Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Brand:</Typography>
                    <Typography fontWeight={600}>{selectedTransfer.brand}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Ski Number:</Typography>
                    <Typography fontWeight={600}>#{selectedTransfer.ski_number}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Type:</Typography>
                    <Typography fontWeight={600}>{selectedTransfer.type}</Typography>
                  </Box>
                </Stack>
              </Box>

              {(selectedTransfer.include_tests || selectedTransfer.include_races) && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>Included Data</Typography>
                  <Stack spacing={1}>
                    {selectedTransfer.include_tests && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Typography>
                          Test data ({selectedTransfer.test_count || 0} test{selectedTransfer.test_count !== 1 ? 's' : ''})
                        </Typography>
                        <Chip 
                          label="Weather, location, rank only" 
                          size="small" 
                          variant="outlined"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                    )}
                    {selectedTransfer.include_races && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Typography>
                          Race data ({selectedTransfer.race_count || 0} race{selectedTransfer.race_count !== 1 ? 's' : ''})
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}

              <Alert severity="warning">
                Once you accept, this ski will be added to your fleet. If test or race data is included, it will be added to your records.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDetailOpen(false)} 
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="outlined"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : <CancelIcon />}
          >
            Reject
          </Button>
          <Button
            onClick={handleAccept}
            color="success"
            variant="contained"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {processing ? 'Processing...' : 'Accept'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

